import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import Stats from '../../core/components/Stats.tsx';
import LegendItem from './LegendItem.tsx';
import { FilterGroup } from '../../core/components/Filter.tsx';
import ChartCard from './ChartCard.tsx';
import Chart from './Chart.tsx';
import { aggregateByMonth, formatDate, formatDateFull, getTicks } from '../utils/chartUtils.ts';
import TooltipContent from './TooltipContent.tsx';
import { ChartCardMode } from './ChartCard.tsx';
import useSearchParamState from '../../core/utils/useSearchParamState.tsx';

interface ChartProps {
    data: any[];
    [key: string]: any;
}

const MODE_PARAM_KEY = 'field-extraction-chart-view';

const FieldExtractionCard = ({
    data,
    ...props
}: Omit<React.ComponentPropsWithRef<typeof ChartCard>, 'title' | 'helpText' | 'controls'> & { data: any[] }) => {
    const { t } = useTranslation('analytics');

    const [mode, setMode] = useSearchParamState<ChartCardMode>(MODE_PARAM_KEY, ChartCardMode.OVER_TIME);

    const isDataValid = data != null && Array.isArray(data) && data.length > 0;

    return (
        <ChartCard
            title={t('dashboard.charts.fieldExtractionCard.title')}
            helpText={t('dashboard.charts.fieldExtractionCard.helpText')}
            controls={
                <FilterGroup>
                    <FilterGroup.Button
                        onClick={() => setMode(ChartCardMode.ACCUMULATED)}
                        active={mode === ChartCardMode.ACCUMULATED}
                    >
                        {t('dashboard.charts.fieldExtractionCard.accumulated')}
                    </FilterGroup.Button>
                    <FilterGroup.Button
                        onClick={() => setMode(ChartCardMode.OVER_TIME)}
                        active={mode === ChartCardMode.OVER_TIME}
                    >
                        {t('dashboard.charts.fieldExtractionCard.overTime')}
                    </FilterGroup.Button>
                </FilterGroup>
            }
            {...props}
        >
            {isDataValid &&
                (mode === ChartCardMode.ACCUMULATED ? (
                    <FieldExtractionStats data={data} />
                ) : (
                    <FieldExtractionChart data={data} />
                ))}
        </ChartCard>
    );
};

export default FieldExtractionCard;

const FieldExtractionStats = ({ data }: ChartProps) => {
    const { t } = useTranslation('analytics');

    const headerAutomatedCount = data?.reduce((acc, item) => acc + (item.headerAutomatedCount || 0), 0);
    const headerAssistedCount = data?.reduce((acc, item) => acc + (item.headerAssistedCount || 0), 0);
    const headerTotalCount = headerAutomatedCount + headerAssistedCount;

    const lineItemsAutomatedCount = data?.reduce((acc, item) => acc + (item.lineItemsAutomatedCount || 0), 0);
    const lineItemsAssistedCount = data?.reduce((acc, item) => acc + (item.lineItemsAssistedCount || 0), 0);
    const lineItemsTotalCount = lineItemsAutomatedCount + lineItemsAssistedCount;

    const totalCount = headerAutomatedCount + headerAssistedCount + lineItemsAutomatedCount + lineItemsAssistedCount;

    return (
        <Stats>
            <Stats.Bar>
                <Stats.BarSection count={headerAutomatedCount} totalCount={totalCount} className="bg-data-data1" />
                <Stats.BarSection count={headerAssistedCount} totalCount={totalCount} className="bg-data-data2" />
                <Stats.BarSection count={lineItemsAutomatedCount} totalCount={totalCount} className="bg-data-data3" />
                <Stats.BarSection count={lineItemsAssistedCount} totalCount={totalCount} className="bg-data-data4" />
            </Stats.Bar>

            <Stats.Group>
                <Stats.Row>
                    <Stats.Label>
                        <LegendItem color="data1">
                            {t('dashboard.charts.fieldExtractionCard.headerAutomated')}
                        </LegendItem>
                    </Stats.Label>

                    <Stats.Value count={headerAutomatedCount} totalCount={headerTotalCount} />
                </Stats.Row>

                <Stats.Row>
                    <Stats.Label>
                        <LegendItem color="data2">
                            {t('dashboard.charts.fieldExtractionCard.headerAssisted')}
                        </LegendItem>
                    </Stats.Label>

                    <Stats.Value count={headerAssistedCount} totalCount={headerTotalCount} />
                </Stats.Row>

                <Stats.Divider />

                <Stats.Row>
                    <Stats.Label>{t('dashboard.charts.fieldExtractionCard.total')}</Stats.Label>
                    <Stats.Value count={headerTotalCount} totalCount={headerTotalCount} />
                </Stats.Row>
            </Stats.Group>

            <Stats.Group>
                <Stats.Row>
                    <Stats.Label>
                        <LegendItem color="data3">
                            {t('dashboard.charts.fieldExtractionCard.lineItemsAutomated')}
                        </LegendItem>
                    </Stats.Label>

                    <Stats.Value count={lineItemsAutomatedCount} totalCount={lineItemsTotalCount} />
                </Stats.Row>

                <Stats.Row>
                    <Stats.Label>
                        <LegendItem color="data4">
                            {t('dashboard.charts.fieldExtractionCard.lineItemsAssisted')}
                        </LegendItem>
                    </Stats.Label>

                    <Stats.Value count={lineItemsAssistedCount} totalCount={lineItemsTotalCount} />
                </Stats.Row>

                <Stats.Divider />

                <Stats.Row>
                    <Stats.Label>{t('dashboard.charts.fieldExtractionCard.total')}</Stats.Label>
                    <Stats.Value count={lineItemsTotalCount} totalCount={lineItemsTotalCount} />
                </Stats.Row>
            </Stats.Group>

            <Stats.Group className="bg-surface-transparent py-1">
                <Stats.Row>
                    <Stats.Label>{t('dashboard.charts.fieldExtractionCard.total')}</Stats.Label>
                    <Stats.Value count={totalCount} totalCount={totalCount} />
                </Stats.Row>
            </Stats.Group>
        </Stats>
    );
};

const FieldExtractionChart = ({ data }: ChartProps) => {
    const { t, i18n } = useTranslation('analytics');

    const dayCount = data.length;
    const isMonthly = dayCount > 32;
    const processedData = isMonthly
        ? aggregateByMonth(data, [
              'headerAutomatedCount',
              'headerAssistedCount',
              'lineItemsAutomatedCount',
              'lineItemsAssistedCount',
          ])
        : data;
    const height = 400;
    const barSize = Math.max(20, Math.min(80, processedData.length <= 7 ? 60 : processedData.length <= 15 ? 40 : 30));

    const maxValue = Math.max(
        ...processedData.map((d) =>
            Math.max(
                d.headerAutomatedCount || 0,
                d.headerAssistedCount || 0,
                d.lineItemsAutomatedCount || 0,
                d.lineItemsAssistedCount || 0
            )
        )
    );
    // round to the next value that is dividable by 10 and by 4
    const factor = 20;
    const maxDisplayValue = Math.ceil(maxValue / factor) * factor;

    return (
        <Chart>
            <Chart.Legend>
                <LegendItem className="text-content-medium" color="data1">
                    {t('dashboard.charts.fieldExtractionCard.headerAutomated')}
                </LegendItem>

                <LegendItem className="text-content-medium" color="data2">
                    {t('dashboard.charts.fieldExtractionCard.headerAssisted')}
                </LegendItem>

                <LegendItem className="text-content-medium" color="data3">
                    {t('dashboard.charts.fieldExtractionCard.lineItemsAutomated')}
                </LegendItem>

                <LegendItem className="text-content-medium" color="data4">
                    {t('dashboard.charts.fieldExtractionCard.lineItemsAssisted')}
                </LegendItem>
            </Chart.Legend>

            <Chart.Content>
                <Chart.FixedSide>
                    <BarChart
                        data={processedData}
                        margin={{ top: 0, left: 0, right: 0, bottom: 10 }}
                        width={70}
                        height={height}
                    >
                        <XAxis
                            dataKey="date"
                            width={0}
                            axisLine={false}
                            tickLine={false}
                            fill="transparent"
                            stroke="transparent"
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            className="text-xs fill-content-strong"
                            domain={[0, maxDisplayValue]}
                            allowDecimals={false}
                            ticks={getTicks(5, maxDisplayValue)}
                            tickFormatter={(value) => value.toLocaleString(i18n.language)}
                        />
                        <Bar dataKey="automated" fill="transparent" stroke="transparent" />
                    </BarChart>
                </Chart.FixedSide>

                <Chart.Scrollable>
                    <ResponsiveContainer
                        width={processedData.length < 10 ? '100%' : processedData.length * 200}
                        height={height}
                    >
                        <BarChart
                            data={processedData}
                            margin={{ right: 30, bottom: 10 }}
                            width={processedData.length * 200}
                            height={height}
                        >
                            <CartesianGrid vertical={false} stroke="inherit" className="stroke-line-weak" />
                            <RechartsTooltip
                                cursor={<Chart.TooltipCursor />}
                                content={({ payload }) => {
                                    const date = payload?.[0]?.payload.date;
                                    const formattedDate = date && formatDateFull(i18n.language, date, isMonthly);

                                    return (
                                        <TooltipContent>
                                            <TooltipContent.Title>{formattedDate}</TooltipContent.Title>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data1">
                                                    {t('dashboard.charts.fieldExtractionCard.headerAutomated')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {payload?.[0]?.payload.headerAutomatedCount || 0}
                                                </span>
                                            </TooltipContent.Row>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data2">
                                                    {t('dashboard.charts.fieldExtractionCard.headerAssisted')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {payload?.[0]?.payload.headerAssistedCount || 0}
                                                </span>
                                            </TooltipContent.Row>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data3">
                                                    {t('dashboard.charts.fieldExtractionCard.lineItemsAutomated')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {payload?.[0]?.payload.lineItemsAutomatedCount || 0}
                                                </span>
                                            </TooltipContent.Row>
                                            <TooltipContent.Row>
                                                <LegendItem className="text-content-white" color="data4">
                                                    {t('dashboard.charts.fieldExtractionCard.lineItemsAssisted')}
                                                </LegendItem>
                                                <span className="font-medium">
                                                    {payload?.[0]?.payload.lineItemsAssistedCount || 0}
                                                </span>
                                            </TooltipContent.Row>
                                        </TooltipContent>
                                    );
                                }}
                            />
                            <XAxis
                                type="category"
                                dataKey="date"
                                tick={{ fill: 'inherit' }}
                                className="text-xs fill-content-medium"
                                tickFormatter={(date) => formatDate(i18n.language, date, isMonthly)}
                                tickMargin={20}
                                minTickGap={20}
                                tickLine={false}
                                axisLine={false}
                                padding={{ left: 20, right: 20 }}
                            />
                            <YAxis domain={[0, maxDisplayValue]} ticks={getTicks(5, maxDisplayValue)} hide />
                            <Bar dataKey="headerAutomatedCount" className="fill-data-data1" barSize={barSize} />
                            <Bar dataKey="headerAssistedCount" className="fill-data-data2" barSize={barSize} />
                            <Bar dataKey="lineItemsAutomatedCount" className="fill-data-data3" barSize={barSize} />
                            <Bar dataKey="lineItemsAssistedCount" className="fill-data-data4" barSize={barSize} />
                        </BarChart>
                    </ResponsiveContainer>
                </Chart.Scrollable>
            </Chart.Content>
        </Chart>
    );
};
